import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../ui/dialog";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { Textarea } from "../../../ui/textarea";
import { Switch } from "../../../ui/switch";
import { toast } from "sonner";
import { CateringUpgrade } from "../../../../hooks/useCateringMenus";

interface EditUpgradeDialogProps {
  upgrade: CateringUpgrade | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (upgrade: any, type: string, updateID?: string) => void;
}

export function EditUpgradeDialog({ upgrade, open, onOpenChange, onSave }: EditUpgradeDialogProps) {
  const isNew = !upgrade?.id;
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [category, setCategory] = useState("mains");
  const [pricePerPax, setPricePerPax] = useState("");
  const [flatPrice, setFlatPrice] = useState("");

  useEffect(() => {
    if (upgrade) {
      setName(upgrade.name);
      setDescription(upgrade.description || "");
      setImageUrl(upgrade.image_url || "");
      setCategory(upgrade.category);
      setPricePerPax(upgrade.price_per_pax?.toString() || "");
      setFlatPrice(upgrade.flat_price?.toString() || "");
    } else {
      setName("");
      setDescription("");
      setImageUrl("");
      setCategory("mains");
      setPricePerPax("");
      setFlatPrice("");
    }
  }, [upgrade, open]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        image_url: imageUrl.trim() || null,
        category: category.trim(),
        price_per_pax: pricePerPax ? parseFloat(pricePerPax) : null,
        flat_price: flatPrice ? parseFloat(flatPrice) : null,
      };
      if (isNew) {
        onSave(payload, 'add', '')      
      }else {
        onSave(payload, 'update', upgrade.id)
      }
        onOpenChange(false);
    } catch (err: any) {
      toast.error(err.message || "Failed to save upgrade");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isNew ? "Add Upgrade" : "Edit Upgrade"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Premium Wagyu Beef" />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          </div>

          <div className="space-y-2">
            <Label>Category</Label>
            <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. mains, desserts" />
            <p className="text-xs text-muted-foreground">Group upgrades by category (e.g. mains, desserts, beverages)</p>
          </div>

          <div className="space-y-2">
            <Label>Image URL</Label>
            <Input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://..." />
            {imageUrl && (
              <img src={imageUrl} alt="Preview" className="w-16 h-16 rounded object-cover mt-1" />
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Price Per Pax ($)</Label>
              <Input type="number" step="0.01" min="0" value={pricePerPax} onChange={(e) => setPricePerPax(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Flat Price ($)</Label>
              <Input type="number" step="0.01" min="0" value={flatPrice} onChange={(e) => setFlatPrice(e.target.value)} />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : isNew ? "Add Upgrade" : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
